import {
  addToast,
  Button,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useValidations } from '../../../hooks/useValidations';
import { TranslationsKeys } from '../../../locales/translations';
import { createStudentAction } from '../actions/studentActions';

export type StudentActionCreateModalMethods = {
  open: () => void;
  close: () => void;
};

type StudentActionCreateModalProps = {
  studentId: number | string;
  onSave?: () => void;
  groupTypeOptions?: SelectOptionType[];
  actionTypesOptions?: SelectOptionType[];
};

type FormFields = {
  groupType: SelectOptionType | null;
  actionType: SelectOptionType | null;
  owner: string;
  notes: string;
};

const DEFAULT_VALUES: FormFields = {
  groupType: null,
  actionType: null,
  owner: '',
  notes: '',
};

const StudentActionCreateModal = (
  {
    onSave,
    studentId,
    actionTypesOptions = [],
    groupTypeOptions = [],
  }: StudentActionCreateModalProps,
  ref: Ref<StudentActionCreateModalMethods>,
) => {
  const { msgValidations } = useValidations();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_OBSERVATIONS,
  );
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const [notesValue] = watch(['notes']);

  const notesCount = useMemo(() => {
    return notesValue?.length ?? 0;
  }, [notesValue]);

  const handleOpen = useCallback(() => {
    setVisible(true);
    reset(DEFAULT_VALUES);
  }, [reset]);

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    async (data: FormFields) => {
      if (loading) {
        return;
      }

      setLoading(true);
      try {
        const response = await createStudentAction(studentId, {
          groupType: String(data.groupType?.value),
          actionType: String(data.actionType?.value),
          owner: data.owner,
          notes: data.notes,
        });

        if (!response.error) {
          addToast({
            color: 'success',
            text: t(`studentActionsModal.success`),
          });
          onSave?.();
          setVisible(false);
        } else {
          addToast({
            color: 'danger',
            text: t(`studentActionsModal.failed`),
          });
        }
      } catch (error) {
        console.error('Error creating student action:', error);
        addToast({
          color: 'danger',
          text: t(`studentActionsModal.failed`),
        });
      } finally {
        setLoading(false);
      }
    },
    [loading, studentId, onSave, t],
  );

  if (!visible) {
    return <></>;
  }

  return (
    <Modal isOpen={true} size="lg" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center">
              {t(`studentActionsModal.title`)}
            </h2>
            <p className="fs-16 w-100 text-center">
              {t(`studentActionsModal.description`)}
            </p>
          </Col>
        </Row>
        <Row className="my-5">
          <Col xs={12} md={4}>
            <Select
              control={control}
              name="groupType"
              options={groupTypeOptions}
              label={t(`studentActionsModal.groupType`)}
              isClearable={false}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              control={control}
              name="actionType"
              options={actionTypesOptions}
              label={t(`studentActionsModal.actionType`)}
              isClearable={false}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col xs={12} md={4}>
            <TextInput
              control={control}
              name="owner"
              label={t(`studentActionsModal.owner`)}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col xs={12} md={12}>
            <TextAreaInput
              control={control}
              name="notes"
              label={t(`studentActionsModal.notes`)}
              rules={{
                required: msgValidations.required,
              }}
              disabled={loading}
              maxLength={200}
            />
            <p>{`${notesCount} / 200`}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.cancel')}
              type="button"
              fullwidth
              outlined
              onClick={handleClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.add')}
              type="button"
              fullwidth
              onClick={handleSubmit(handleSave)}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(StudentActionCreateModal);
